import type { ApprovalState } from '@/generated/http-clients/v3ApiSchemas'
import { Box, Stack, Text, Tooltip } from '@chakra-ui/react'
import {
  CircleAlert,
  CircleCheck,
  FileCheck,
  FilePlus,
  FileX,
} from 'lucide-react'
import type { PropsWithChildren } from 'react'

type RevisionStateProps = {
  approvalState: ApprovalState
  size?: 'sm' | 'md' // default size: 'sm' (1rem)
}

type Props = {
  approvalState: ApprovalState
  hoverText?: string
}

function HoverText({ approvalState, hoverText }: Readonly<Props>) {
  return (
    <Stack gap={0}>
      <Text color="white" fontWeight="bold" textTransform="capitalize">
        {hoverText ?? approvalState.state}
      </Text>
      <Text color="gray" fontSize="xs">
        Approval state: {approvalState.state}
      </Text>
    </Stack>
  )
}

export function RevisionState({
  /**
   * The revision's approval state.
   */
  approvalState,
  size,
}: PropsWithChildren<RevisionStateProps>) {
  const iconSize = size === 'md' ? '1.5rem' : '1rem'

  function renderContent() {
    if (approvalState.state === 'SUBMITTED') {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <FilePlus size={iconSize} color="gray" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText approvalState={approvalState} />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (approvalState.state === 'APPROVED') {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <FileCheck size={iconSize} color="green" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText approvalState={approvalState} />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (approvalState.state === 'REJECTED') {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <FileX size={iconSize} color="red" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText approvalState={approvalState} />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (approvalState.state === 'APPLIED') {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <CircleCheck size={iconSize} color="green" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText approvalState={approvalState} />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <CircleAlert size={iconSize} color="orange" />
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>
            <Tooltip.Arrow />
            <HoverText approvalState={approvalState} hoverText="Unknown" />
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>
    )
  }

  return <Box paddingY="0.25rem">{renderContent()}</Box>
}
